// One-time Terms acceptance prompt.
//
// The signup form has always gated on a checkbox, but "Continue with Google"
// creates an account too — from /signin as much as /signup — so members exist
// who never saw the terms at all. This asks them once, records it, and never
// appears again until LEGAL_VERSION changes.
//
// Deliberately fails OPEN. If the profile cannot be read (cold lambda, network
// blip, an error like the blank-phone 500 that broke voice onboarding) we show
// nothing rather than lock a paying member out of the product behind a modal
// they cannot dismiss.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import api from "../services/api";
import { API_PATHS } from "../config/apiConfig";
import { LEGAL_VERSION } from "./legal-docs";
import {
  flushPendingTermsAcceptance, needsTermsAcceptance, recordTermsAcceptance,
} from "../services/terms";
import log from "../utils/logger";

export default function TermsGate() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { pathname } = useLocation();

  // Never cover the documents themselves. The prompt's own link opens /legal
  // in a new tab, and that tab runs this component too — without this, the
  // member is asked to read terms that a modal is sitting on top of.
  const onLegalPage = pathname.startsWith("/legal");

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        await Auth.currentAuthenticatedUser();
      } catch (e) {
        return; // signed out — nothing to gate
      }
      // A member who just accepted on the signup form has their acceptance
      // parked in sessionStorage; write it first so they are never asked twice.
      await flushPendingTermsAcceptance();
      try {
        const { data } = await api.get(API_PATHS.USERS.PROFILE);
        if (!cancelled && needsTermsAcceptance(data)) setShow(true);
      } catch (e) {
        log.error("terms gate: profile unreadable, not prompting", { error: e });
      }
    };
    check();
    // The Google redirect authenticates AFTER this mounts, and the signup flow
    // signs in without a reload — so re-check on the sign-in event too, or the
    // one path that most needs this gate would never see it.
    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signIn") check();
      else if (payload.event === "signOut") setShow(false);
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  const accept = useCallback(async () => {
    setBusy(true); setErr("");
    const ok = await recordTermsAcceptance(LEGAL_VERSION);
    setBusy(false);
    if (ok) setShow(false);
    else setErr("Couldn't save that — please try again.");
  }, []);

  if (!show || onLegalPage) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
      style={{
        position: "fixed", inset: 0, zIndex: 9999, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
        background: "rgba(8,9,12,0.72)", backdropFilter: "blur(2px)",
      }}
    >
      <div style={{
        maxWidth: 460, width: "100%", background: "#fff", borderRadius: 14,
        padding: "26px 28px", boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
      }}>
        <h2 id="terms-gate-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          One quick thing
        </h2>
        <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6, color: "#4b5563" }}>
          We've updated our Terms of Service and Privacy Policy, and we don't
          have your agreement on file. Please read them and accept to carry on
          using Glonet.
        </p>
        <p style={{ marginTop: 10, fontSize: 14 }}>
          <Link
            to="/legal"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4f46e5", fontWeight: 600 }}
          >
            Read the Terms of Service and Privacy Policy
          </Link>
        </p>
        {err && <p style={{ marginTop: 10, fontSize: 13, color: "#dc2626" }}>{err}</p>}
        <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => Auth.signOut()}
            style={{
              fontSize: 14, padding: "9px 16px", borderRadius: 8, cursor: "pointer",
              background: "transparent", border: "1px solid #d1d5db", color: "#374151",
            }}
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            style={{
              fontSize: 14, fontWeight: 600, padding: "9px 18px", borderRadius: 8,
              cursor: busy ? "wait" : "pointer", background: "#4f46e5",
              border: "none", color: "#fff", opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Saving…" : "I agree"}
          </button>
        </div>
      </div>
    </div>
  );
}
